import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocations } from '../hooks/useLocations';
import { locationSiblingPath } from '../lib/locationPaths';

export function LocationSwitcher({ locationId }: { locationId: string }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data } = useLocations();
  const locations = data?.locations ?? [];
  const current = locations.find((location) => location.id === locationId);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!current || locations.length < 2) return null;

  return (
    <div ref={ref} className="relative border-b border-border bg-bg-muted px-4 py-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 text-xs text-text-muted hover:text-text"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>Site:</span>
        <span className="font-medium text-text">{current.name}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute left-4 top-full z-30 mt-1 max-h-72 w-72 overflow-y-auto rounded-lg border border-border bg-surface shadow-lg"
        >
          {locations.map((location) => {
            const isActive = location.id === locationId;
            return (
              <li key={location.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    setOpen(false);
                    if (!isActive) navigate(locationSiblingPath(pathname, locationId, location.id));
                  }}
                  className={`w-full px-3 py-2 text-left hover:bg-bg-muted ${isActive ? 'bg-bg-muted' : ''}`}
                >
                  <div className="text-sm font-medium text-text">{location.name}</div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
